import { getApiClient } from "@punchup/utils";
import type {
  AyrshareProfileRow,
  LinkedAccount,
  SocialPlatform,
  PostRequest,
  PerPlatformResult,
  CrossPostRow,
  Pagination,
} from "../../models/ayrshare";

// Browser-safe wrappers for the /api/v2/manager/social/* routes (DATA-05).
// Every call goes through getApiClient() — zero raw fetch (CLAUDE.md rule).
// No client-side Zod parsing (server guarantees schemas via Phase 1 D-06 /
// Phase 2 PostRequestSchema / etc.) — types imported from models/ayrshare.
// No try/catch + logException in wrappers — follows ticketing.ts, NOT
// credits.ts. Hooks handle errors via React Query's `error` field (CONTEXT.md D-10).
// Signature deviation from CONTEXT.md `<domain>` text, resolved per server:
//   - fetchCrossPostHistory uses zero-indexed `page` + `pageSize` (NOT `per_page`).

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

/**
 * Fetch the Ayrshare profile + linked accounts for a comedian.
 * Returns `{ profile: null, connectedAccounts: [] }` when no profile exists.
 */
export async function fetchSocialProfile(comedianId: string): Promise<{
  profile: AyrshareProfileRow | null;
  connectedAccounts: LinkedAccount[];
}> {
  const searchParams = new URLSearchParams({ comedianId });
  const response = await getApiClient().get<{
    data: {
      profile: AyrshareProfileRow | null;
      connectedAccounts: LinkedAccount[];
    };
  }>(`/api/v2/manager/social/profiles?${searchParams.toString()}`);
  return response.data;
}

/**
 * Create (or return existing) Ayrshare profile for a comedian. Server is idempotent.
 */
export async function createOrUpdateSocialProfile(input: {
  comedianId: string;
}): Promise<AyrshareProfileRow> {
  const response = await getApiClient().post<{
    data: { profile: AyrshareProfileRow };
  }>("/api/v2/manager/social/profiles", input);
  return response.data.profile;
}

// ---------------------------------------------------------------------------
// Connect / Disconnect
// ---------------------------------------------------------------------------

/**
 * Generate a fresh Ayrshare-hosted JWT URL for the user to open in a new window.
 * One-shot — no local state change; hook does not invalidate caches (D-05).
 */
export async function generateConnectJwtUrl(
  comedianId: string,
  platform?: SocialPlatform,
  redirectUrl?: string
): Promise<string> {
  const response = await getApiClient().post<{ data: { url: string } }>(
    "/api/v2/manager/social/connect",
    { comedianId, platform, redirectUrl }
  );
  return response.data.url;
}

/**
 * Unlink a platform for a comedian. Server updates social_accounts jsonb.
 */
export async function disconnectSocialAccount(
  comedianId: string,
  platform: SocialPlatform
): Promise<{ success: true }> {
  const response = await getApiClient().post<{
    data: { success: true };
  }>("/api/v2/manager/social/disconnect", { comedianId, platform });
  return response.data;
}

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------

/**
 * Submit an immediate or scheduled cross-post.
 * Caller MUST include an idempotencyKey (UUID v4) — the hook generates it
 * (CONTEXT.md D-07). Partial-failure is a 200 with `post.status === "partial_failed"`
 * (D-18) — UI reads post.status + perPlatform[] to render per-platform chips.
 * NOTE: server does NOT auto-retry 5xx (D-11, D-31); do NOT add retry config
 * in the calling hook — let React Query's default (no retry on mutations) apply.
 */
export async function submitCrossPost(
  input: PostRequest
): Promise<{ post: CrossPostRow; perPlatform: PerPlatformResult[] }> {
  const response = await getApiClient().post<{
    data: { post: CrossPostRow; perPlatform: PerPlatformResult[] };
  }>("/api/v2/manager/social/posts", input);
  return response.data;
}

// ---------------------------------------------------------------------------
// Clip (trim)
// ---------------------------------------------------------------------------

/**
 * Create a trimmed Cloudflare Stream clip for an uploaded video. Used by the
 * cross-posting wizard's trim affordance. The returned `playbackUrl` is shaped
 * like the dropzone's upload URL, so callers can swap it into `upload.mediaUrl`
 * directly. `watermark` is opt-in (default off).
 */
export async function createCrossPostClip(input: {
  comedianId: string;
  videoId: string;
  startTime: number;
  endTime: number;
  watermark?: boolean;
}): Promise<{ clipVideoId: string; playbackUrl: string }> {
  const response = await getApiClient().post<{
    data: { clipVideoId: string; playbackUrl: string };
  }>("/api/v2/manager/social/clip", input);
  return response.data;
}

/**
 * Paginated history of cross-posts for a comedian.
 * `page` is zero-indexed (server clamps Math.max(0, …)). `pageSize` defaults to 20
 * and is clamped to [1, 100] server-side. Param name is `pageSize` — NOT
 * `per_page` like ticketing.ts uses (PATTERNS.md deviation #1).
 */
export async function fetchCrossPostHistory(
  comedianId: string,
  page: number = 0,
  pageSize: number = 20
): Promise<{ posts: CrossPostRow[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams({
    comedianId,
    page: page.toString(),
    pageSize: pageSize.toString(),
  });
  const response = await getApiClient().get<{
    data: { posts: CrossPostRow[]; pagination: Pagination };
  }>(`/api/v2/manager/social/posts?${searchParams.toString()}`);
  return response.data;
}
